import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { GrowthTimelineResult } from '../../types/financialPlan';
import { SECTION_HINTS } from '../../utils/financialPlanDefaults';
import {
  exportPlanDebugCsv,
  formatPlanDebugCell,
  isPlanDebugCheckOk,
  PLAN_DEBUG_COLUMN_GROUPS,
  PlanDebugColumnGroup,
  PlanDebugDollarMode,
  PlanYearDebugColumn,
  PlanYearDebugRow,
  visiblePlanDebugColumns,
} from '../../utils/lgcPlanDebug';
import './LgcPlanDebugTable.css';

const DEFAULT_ENABLED_GROUPS = new Set<PlanDebugColumnGroup>([
  'meta',
  'income',
  'spend',
  'savings',
  'affordability',
  'accounts_balances',
  'accounts_flows',
  'required_income',
]);

function columnHeaderLabel(col: PlanYearDebugColumn, dollarMode: PlanDebugDollarMode): string {
  if (col.dollarMode === 'today') return `${col.label} (today)`;
  if (col.dollarMode === 'nominal') return `${col.label} (nom.)`;
  return col.label;
}

function rowHasWarning(row: PlanYearDebugRow): boolean {
  if (row.phase === 'accumulation' && row.incomeGapToday < -1) return true;
  if (row.phase === 'accumulation' && row.surplusToday < -1) return true;
  if (!isPlanDebugCheckOk(row.checkSurplus)) return true;
  if (!isPlanDebugCheckOk(row.checkRequiredStack)) return true;
  if (!isPlanDebugCheckOk(row.checkPool)) return true;
  return false;
}

interface LgcPlanDebugTableProps {
  timeline: GrowthTimelineResult;
  rows: PlanYearDebugRow[];
}

const LgcPlanDebugTable: React.FC<LgcPlanDebugTableProps> = ({ timeline, rows }) => {
  const [dollarMode, setDollarMode] = useState<PlanDebugDollarMode>('today');
  const [enabledGroups, setEnabledGroups] = useState<Set<PlanDebugColumnGroup>>(
    () => new Set(DEFAULT_ENABLED_GROUPS),
  );

  const columns = useMemo(
    () => visiblePlanDebugColumns(dollarMode, enabledGroups),
    [dollarMode, enabledGroups],
  );

  const toggleGroup = (group: PlanDebugColumnGroup) => {
    setEnabledGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const downloadCsv = () => {
    const csv = exportPlanDebugCsv(rows, columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lgc-plan-debug-${timeline.currentAge}-${timeline.endAge}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ops-card lgc-section-card lgc-debug-card">
      <div className="lgc-debug-header">
        <div className="lgc-debug-header-text">
          <h2 className="lgc-section-title">Plan Debug</h2>
          {SECTION_HINTS.debug && <p className="lgc-section-hint">{SECTION_HINTS.debug}</p>}
          <p className="lgc-debug-meta">
            Ages {timeline.currentAge}–{timeline.endAge} · {rows.length} years · corpus goal{' '}
            <span className="lgc-mono">${Math.round(timeline.corpusNeeded).toLocaleString()}</span>
          </p>
        </div>
        <div className="lgc-debug-toolbar">
          <div className="lgc-debug-dollar-mode" role="radiogroup" aria-label="Dollar display mode">
            {(['today', 'nominal', 'both'] as PlanDebugDollarMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`lgc-debug-dollar-btn${dollarMode === mode ? ' active' : ''}`}
                aria-pressed={dollarMode === mode}
                onClick={() => setDollarMode(mode)}
              >
                {mode === 'today' ? "Today's $" : mode === 'nominal' ? 'Nominal' : 'Both'}
              </button>
            ))}
          </div>
          <button type="button" className="lgc-debug-export-btn" onClick={downloadCsv}>
            <Download size={14} aria-hidden />
            Export CSV
          </button>
        </div>
      </div>

      <div className="lgc-debug-groups" role="group" aria-label="Column groups">
        {PLAN_DEBUG_COLUMN_GROUPS.map((g) => (
          <label key={g.id} className="lgc-debug-group-chip">
            <input
              type="checkbox"
              checked={enabledGroups.has(g.id)}
              onChange={() => toggleGroup(g.id)}
            />
            {g.label}
          </label>
        ))}
      </div>

      <div className="lgc-debug-table-wrap">
        <table className="lgc-debug-table">
          <thead>
            <tr>
              <th className="lgc-debug-table-sticky lgc-debug-table-sticky--age" scope="col">
                Age
              </th>
              {columns.map((col) => (
                <th key={col.key} scope="col" title={col.group}>
                  {columnHeaderLabel(col, dollarMode)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const milestoneRow = row.milestones.length > 0;
              const warn = rowHasWarning(row);
              return (
                <tr
                  key={row.age}
                  className={[
                    milestoneRow ? 'lgc-debug-row--milestone' : '',
                    warn ? 'lgc-debug-row--warn' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <th scope="row" className="lgc-debug-table-sticky lgc-debug-table-sticky--age">
                    {row.age}
                  </th>
                  {columns.map((col) => {
                    const raw = row[col.key];
                    const isCheck = col.group === 'checks';
                    const checkBad = isCheck && typeof raw === 'number' && !isPlanDebugCheckOk(raw);
                    const gapBad =
                      col.key === 'incomeGapToday' && typeof raw === 'number' && raw < -1;
                    const surplusBad =
                      col.key === 'surplusToday' && typeof raw === 'number' && raw < -1;
                    return (
                      <td
                        key={col.key}
                        className={[
                          col.format !== 'text' ? 'lgc-mono' : '',
                          checkBad || gapBad || surplusBad ? 'lgc-debug-cell--bad' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {formatPlanDebugCell(raw, col.format)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LgcPlanDebugTable;
